import React, { useRef, useEffect } from 'react';
import { Box, Typography, Container, Card, CardContent } from '@mui/material';
import PageTitle from '../../components/common/PageTitle';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO';
import THEME from '../../constants/theme';

// Import Sentinel logo
import sentinelLogo from '../../assets/logos/sentinel/sentinel-blue-logo.svg';

const LoginPage = ({ setRefs }) => {
  const loginRef = useRef(null);

  useEffect(() => {
    setRefs({ login: loginRef });
  }, [setRefs]);

  const handleCardClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const cardVariants = {
    initial: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    animate: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  const logoVariants = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { 
      opacity: 1, 
      scale: 1,
      transition: {
        duration: 0.5,
        delay: 0.2
      }
    },
    hover: {
      scale: 1.05,
      transition: {
        duration: 0.2
      }
    }
  };

  return (
    <>
      <SEO
        title="Login Portal"
        description="Access your Sentinel account through our secure login portal."
        keywords="login, account, signin, sentinel, portal"
      />
      
      <Box 
        ref={loginRef}
        id="login"
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          backgroundColor: 'var(--neutral-white)',
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 6, md: 8 },
          px: { xs: 2, md: 4 }
        }}
      >
        <Container maxWidth="lg">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: 'easeOut' }}>
            <PageTitle title="Login Portal" subtitle="Access your Sentinel account" />
          </motion.div>

          {/* Single Card Container */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              maxWidth: '500px',
              mx: 'auto'
            }}
          >
            {/* Sentinel Card */}
            <motion.div
              variants={cardVariants}
              initial="initial"
              animate="animate"
              whileHover="hover"
              style={{ width: '100%' }}
            >
              <Card
                onClick={() => handleCardClick('https://app.staging.sentinel-risk.co.uk/')}
                sx={{
                  cursor: 'pointer',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '2px solid rgba(169, 64, 255, 0.12)',
                  borderRadius: 3,
                  boxShadow: '0 12px 48px rgba(169, 64, 255, 0.12)',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, var(--primary-purple), rgba(169,64,255,0.3))'
                  },
                  '&:hover': {
                    borderColor: 'rgba(169, 64, 255, 0.3)',
                    boxShadow: '0 20px 64px rgba(169, 64, 255, 0.18)',
                    background: 'rgba(255, 255, 255, 0.98)'
                  }
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: { xs: '40px 20px', md: '50px 40px' },
                    position: 'relative',
                    zIndex: 2
                  }}
                >
                  {/* Sentinel Logo */}
                  <motion.div
                    variants={logoVariants}
                    initial="initial"
                    animate="animate"
                    whileHover="hover"
                    style={{ 
                      marginBottom: '20px',
                      width: '250px',
                      height: '80px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Box
                      component="img"
                      src={sentinelLogo}
                      alt="Sentinel"
                      sx={{
                        height: { xs: '60px', md: '75px' },
                        width: 'auto',
                        maxWidth: '250px',
                        filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))'
                      }}
                    />
                  </motion.div>

                  <Typography
                    variant="body2"
                    sx={{
                      color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                      lineHeight: 1.6,
                      mb: 3,
                      fontSize: '0.95rem',
                      fontWeight: 500
                    }}
                  >
                    Synchronize your trading power
                  </Typography>

                  <Box
                    sx={{
                      mt: 'auto',
                      padding: '12px 40px',
                      borderRadius: '50px',
                      background: 'var(--primary-purple)',
                      color: 'white',
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontWeight: 600,
                      fontSize: '0.95rem',
                      boxShadow: '0 4px 16px rgba(169,64,255,0.25)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 6px 24px rgba(169,64,255,0.35)'
                      }
                    }}
                  >
                    Login
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default LoginPage;
